import { useEffect, useRef, useState } from "react";
import type { DefinitionReadiness, GeneratedDefinition, NodeAttributeTarget, NodeAttributeType } from "@keydials/api-client";
import {
  addField,
  askOfEveryNode,
  detailsPlaceOf,
  moveField,
  offeredSettings,
  removeField,
  resolveFieldSelection,
  segmentNoun,
  updateField,
  type BuilderSelection,
  type DetailsPlaceId,
  type FieldSelection,
  type SegmentNoun,
} from "@keydials/dial-workspace";
import { ConfirmRemove } from "../ConfirmRemove";
import { builderCopy as copy } from "../copy";
import { detailsView, fieldRowId, removeConsequences, TYPE_CARDS, type FieldRow, type PlaceView } from "../detailsView";
import type { DraftSession, DraftSnapshot } from "../draftSession";
import { FieldSettings } from "../FieldSettings";
import { moreOptionsLabel } from "../fieldSettingsView";
import { readinessView } from "../readiness";
import { ReadinessPill } from "../ReadinessPill";

interface DetailsStageProps {
  session: DraftSession;
  snapshot: DraftSnapshot;
  readiness: DefinitionReadiness;
  /** The field to show on arrival: the one "Fix this" pointed at. */
  initialSelection?: FieldSelection | null;
  /** A problem the readiness pill points at on the wheel, which is fixed on Build. */
  onFix: (selection: BuilderSelection) => void;
  /** Skip for now, or Continue once something is asked: on to Launch. */
  onNext: () => void;
}

/** Where the cursor goes next: a field just added, its name selected so typing replaces it, or a field "Fix this" pointed at. */
interface FocusRequest {
  id: string;
  select: boolean;
}

type Edit = (change: (model: GeneratedDefinition) => GeneratedDefinition) => void;

/** The two layouts, both on the page at once: a card per place on a wide screen, an accordion on a phone. Ids carry it. */
type Layout = "wide" | "phone";

/**
 * Details (KEY-560, Phase 3): what people record, and where. Each place (the whole dial, each section, each action point and, on a
 * checklist dial, each item) lists the fields it asks for, which can be renamed, moved and removed, and "+ Add a field" asks what
 * kind of answer a new one takes. More options opens a field's settings under its row. Every change goes through the shared field
 * edits and is autosaved. A first dial is recommended to skip it. On a phone the places become an accordion, like Build's list of
 * sections.
 */
export function DetailsStage({ session, snapshot, readiness, initialSelection, onFix, onNext }: DetailsStageProps) {
  const model = snapshot.model;
  const locked = snapshot.lockedLifecycle !== null;
  const noun = segmentNoun(snapshot.noun ? { segmentNounSingular: snapshot.noun.singular, segmentNounPlural: snapshot.noun.plural } : null);
  const view = detailsView(model, noun);

  const arrival = resolveFieldSelection(model, initialSelection ?? null)?.selection ?? null;
  const arrivalId = arrival ? fieldRowId(arrival.target, arrival.key) : null;
  const [openPlace, setOpenPlace] = useState<DetailsPlaceId | null>(arrival ? detailsPlaceOf(arrival.target) : null);
  const [focus, setFocus] = useState<FocusRequest | null>(arrivalId ? { id: arrivalId, select: false } : null);
  const [picking, setPicking] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  // Whose settings are open, by row, shared by both layouts. A field "Fix this" points at arrives with them open, since what's wrong
  // with a field (the items it's on, its group) is mostly fixed there.
  const [settingsOpen, setSettingsOpen] = useState<ReadonlySet<string>>(() => new Set(arrivalId ? [arrivalId] : []));

  // An edit that changes nothing (a box left with nothing to tidy, a move that can't be made) isn't sent: the session saves on
  // every edit it's given.
  const edit: Edit = (change) => {
    if (!locked && change(model) !== model) session.edit(change);
  };

  function showSettings(id: string, open: boolean) {
    setSettingsOpen((current) => {
      if (current.has(id) === open) return current;
      const next = new Set(current);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function showField(selection: FieldSelection) {
    if (!resolveFieldSelection(model, selection)) return;
    const id = fieldRowId(selection.target, selection.key);
    setOpenPlace(detailsPlaceOf(selection.target));
    showSettings(id, true);
    setFocus({ id, select: false });
  }

  function add(target: NodeAttributeTarget, type: NodeAttributeType, label: string) {
    setPicking(null);
    if (locked) return;
    let key = "";
    session.edit((draft) => {
      const added = addField(draft, { target, type, label });
      key = added.key;
      return added.model;
    });
    if (key) setFocus({ id: fieldRowId(target, key), select: true });
  }

  const placeFields = (place: PlaceView, layout: Layout) => (
    <PlaceFields
      layout={layout}
      place={place}
      model={model}
      noun={noun}
      locked={locked}
      picking={picking === place.target && !locked}
      confirming={confirming}
      settingsOpen={settingsOpen}
      focus={focus}
      onPicking={(open) => setPicking(open ? place.target : null)}
      onAdd={(type, label) => add(place.target, type, label)}
      onConfirming={setConfirming}
      onSettings={showSettings}
      onFocused={() => setFocus(null)}
      edit={edit}
    />
  );

  return (
    <section className="kd-build kd-details" aria-labelledby="kd-builder-question">
      <div className="kd-build-head">
        <h1 id="kd-builder-question" className="kd-builder-question">
          {copy.details.question}
        </h1>
        <ReadinessPill view={readinessView(readiness, model, noun)} onJump={(jump) => (jump.level === "field" ? showField(jump) : onFix(jump))} />
      </div>
      <p className="kd-builder-muted">{view.openingLine}</p>

      <div className="kd-details-places">
        {view.places.map((place) => (
          <section key={place.id} className="kd-selection-panel" aria-labelledby={`kd-details-place-${place.id}`}>
            <div className="kd-details-place-head">
              <h2 id={`kd-details-place-${place.id}`} className="kd-details-place-name">
                {place.name}
              </h2>
              <span className="kd-build-list-meta">{place.count}</span>
            </div>
            {placeFields(place, "wide")}
          </section>
        ))}
      </div>

      <div className="kd-build-list">
        <ol className="kd-build-list-sections">
          {view.places.map((place) => {
            const open = place.id === openPlace;
            return (
              <li key={place.id} className="kd-build-list-section" data-open={open}>
                <button type="button" className="kd-build-list-head" aria-expanded={open} onClick={() => setOpenPlace(open ? null : place.id)}>
                  <span className="kd-build-list-name">{place.name}</span>
                  <span className="kd-build-list-meta">{place.count}</span>
                </button>
                {/* Hidden, not unmounted, so closing a place keeps whatever was half typed in it. */}
                <div className="kd-selection-panel" hidden={!open}>
                  {placeFields(place, "phone")}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {!view.hasFields && <p className="kd-builder-muted">{copy.details.skipHint}</p>}
      <div className="kd-builder-footer">
        <button type="button" className="kd-btn-primary" onClick={onNext}>
          {view.hasFields ? copy.frame.continue : copy.details.skip}
        </button>
      </div>
    </section>
  );
}

interface PlaceFieldsProps {
  layout: Layout;
  place: PlaceView;
  model: GeneratedDefinition;
  noun: SegmentNoun;
  locked: boolean;
  picking: boolean;
  confirming: string | null;
  settingsOpen: ReadonlySet<string>;
  focus: FocusRequest | null;
  onPicking: (open: boolean) => void;
  onAdd: (type: NodeAttributeType, label: string) => void;
  onConfirming: (id: string | null) => void;
  onSettings: (id: string, open: boolean) => void;
  onFocused: () => void;
  edit: Edit;
}

/** One place's hint, its fields under their headings, and the way to add another. The same on a wide screen and on a phone. */
function PlaceFields({ layout, place, model, noun, locked, picking, confirming, settingsOpen, focus, onPicking, onAdd, onConfirming, onSettings, onFocused, edit }: PlaceFieldsProps) {
  // Each name box says which one it is, as a checklist's do, counting through the place's groups.
  const numbers = new Map(place.runs.flatMap((run) => run.rows).map((row, index) => [row.id, index + 1]));
  const addButton = useRef<HTMLButtonElement>(null);
  const [returnFocus, setReturnFocus] = useState(false);

  // Cancelling the picker puts the cursor back where it was. Both layouts are on the page, so only the one showing takes it.
  useEffect(() => {
    const button = addButton.current;
    if (!returnFocus || picking || !button) return;
    setReturnFocus(false);
    if (button.offsetParent !== null) button.focus();
  }, [returnFocus, picking]);

  return (
    <>
      <p className="kd-builder-muted">{place.hint}</p>
      {place.runs.length > 0 && (
        // One list for the place, with each group's heading between its fields, so a row isn't remade when a group edit moves it from
        // one run to another: typing a heading would otherwise lose the cursor, and a rename half typed in another row would be lost.
        <ol className="kd-panel-list kd-details-fields">
          {place.runs.flatMap((run, index) => [
            ...(run.title === null
              ? []
              : [
                  <li key={`heading:${index}`} role="presentation" className="kd-details-run-head">
                    <h3 className="kd-panel-heading">{run.title}</h3>
                  </li>,
                ]),
            ...run.rows.map((row, rowIndex) => (
              <FieldRowItem
                key={row.id}
                layout={layout}
                row={row}
                runStart={index > 0 && rowIndex === 0 && run.title === null}
                number={numbers.get(row.id) ?? 0}
                model={model}
                noun={noun}
                locked={locked}
                confirming={confirming === row.id}
                settingsOpen={settingsOpen.has(row.id)}
                focus={focus?.id === row.id ? focus : null}
                onConfirming={onConfirming}
                onSettings={onSettings}
                onFocused={onFocused}
                edit={edit}
              />
            )),
          ])}
        </ol>
      )}
      {picking ? (
        <TypePicker
          onPick={onAdd}
          onCancel={() => {
            setReturnFocus(true);
            onPicking(false);
          }}
        />
      ) : (
        <div className="kd-panel-actions">
          <button ref={addButton} type="button" className="kd-btn" disabled={locked} onClick={() => onPicking(true)}>
            {copy.details.addField}
          </button>
        </div>
      )}
    </>
  );
}

interface FieldRowItemProps {
  layout: Layout;
  row: FieldRow;
  /** The first loose field after a group, which needs a little room above it to read as outside the group. */
  runStart: boolean;
  /** Its place in the place's list, for the name box's label. */
  number: number;
  model: GeneratedDefinition;
  noun: SegmentNoun;
  locked: boolean;
  confirming: boolean;
  settingsOpen: boolean;
  focus: FocusRequest | null;
  onConfirming: (id: string | null) => void;
  onSettings: (id: string, open: boolean) => void;
  onFocused: () => void;
  edit: Edit;
}

function FieldRowItem({ layout, row, runStart, number, model, noun, locked, confirming, settingsOpen, focus, onConfirming, onSettings, onFocused, edit }: FieldRowItemProps) {
  const input = useRef<HTMLInputElement>(null);
  const moreButton = useRef<HTMLButtonElement>(null);
  const field = model.nodeAttributes?.find((f) => f.target === row.target && f.key === row.key);
  const hasSettings = field !== undefined && offeredSettings(field).length > 0;
  // Unique on the page: the row's id says which field, and the layout which of its two copies.
  const settingsId = `kd-field-${layout}-${encodeURIComponent(row.id)}-settings`;

  // Both layouts are on the page and the stylesheet shows one, so only the name box that's showing takes the cursor. Taking it
  // clears the request, so the row doesn't take it again when an accordion reopens.
  useEffect(() => {
    const box = input.current;
    if (!focus || !box || box.offsetParent === null) return;
    box.scrollIntoView?.({ block: "center" });
    box.focus({ preventScroll: true });
    if (focus.select) box.select();
    onFocused();
  }, [focus, onFocused]);

  return (
    <li className={runStart ? "kd-checklist-item kd-details-run-start" : "kd-checklist-item"}>
      <input
        ref={input}
        className="kd-checklist-name"
        aria-label={`${copy.build.nameLabel} ${number}`}
        value={row.label}
        placeholder={copy.build.untitled}
        disabled={locked}
        onChange={(e) => {
          const label = e.target.value;
          edit((m) => updateField(m, row.target, row.key, { label }));
        }}
      />
      <div className="kd-checklist-controls">
        <span className="kd-details-type">{row.typeLabel}</span>
        <span className="kd-checklist-actions">
          <button type="button" className="kd-icon-btn" disabled={locked || !row.canMoveUp} onClick={() => edit((m) => moveField(m, row.target, row.key, -1))}>
            {copy.build.moveUp}
          </button>
          <button type="button" className="kd-icon-btn" disabled={locked || !row.canMoveDown} onClick={() => edit((m) => moveField(m, row.target, row.key, 1))}>
            {copy.build.moveDown}
          </button>
          <button type="button" className="kd-icon-btn" disabled={locked} onClick={() => onConfirming(row.id)}>
            {copy.build.remove}
          </button>
        </span>
      </div>
      {row.askOfEvery && (
        <button type="button" className="kd-builder-link kd-details-ask" disabled={locked} onClick={() => edit((m) => askOfEveryNode(m, row.target, row.key))}>
          {row.askOfEvery}
        </button>
      )}
      {confirming && (
        <ConfirmRemove
          name={row.label}
          consequences={removeConsequences(removeField(model, row.target, row.key).removed)}
          onKeep={() => onConfirming(null)}
          onRemove={() => {
            onConfirming(null);
            edit((m) => removeField(m, row.target, row.key).model);
          }}
        />
      )}
      {field && hasSettings && (
        <>
          <button
            ref={moreButton}
            type="button"
            className="kd-builder-link kd-details-more"
            aria-expanded={settingsOpen}
            aria-controls={settingsId}
            aria-label={moreOptionsLabel(row.label)}
            onClick={() => onSettings(row.id, !settingsOpen)}
          >
            {copy.build.moreOptions}
          </button>
          <FieldSettings
            id={settingsId}
            hidden={!settingsOpen}
            field={field}
            model={model}
            noun={noun}
            locked={locked}
            edit={edit}
            onDone={() => {
              onSettings(row.id, false);
              moreButton.current?.focus();
            }}
          />
        </>
      )}
    </li>
  );
}

/** "What kind of answer?": a card for each kind the builder offers. Picking one adds a field of that kind, named after it. */
function TypePicker({ onPick, onCancel }: { onPick: (type: NodeAttributeType, label: string) => void; onCancel: () => void }) {
  return (
    <div className="kd-builder-confirm kd-details-picker" role="group" aria-label={copy.details.typePickerHeading}>
      <div className="kd-details-picker-head">
        <h3 className="kd-panel-heading">{copy.details.typePickerHeading}</h3>
        <button type="button" className="kd-btn" onClick={onCancel}>
          {copy.details.cancel}
        </button>
      </div>
      <div className="kd-choice-grid">
        {TYPE_CARDS.map((card, index) => (
          <button key={card.id} type="button" className="kd-choice" autoFocus={index === 0} onClick={() => onPick(card.type, card.label)}>
            <span className="kd-choice-title">{card.label}</span>
            <span className="kd-choice-body">{card.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
